import React, { useState, useRef, useEffect } from "react";
import logo from "./../../image/logo.png";
import { useNavigate } from "react-router-dom";
import showToast from "@utils/toast";
import { isTokenExpired } from "@utils/authUtils";

export default function OtpLogin() {
  const navigate = useNavigate();

  const useEmail = localStorage.getItem("user_email");
  const sessionId = localStorage.getItem("loginSessionId");

  // Redirect immediately if already logged in
  const token = localStorage.getItem("accessToken");
  useEffect(() => {
    if (token && !isTokenExpired(token)) {
      navigate("/dashboard");
    }
  }, [token, navigate]);

  const [otp, setOtp] = useState(Array(6).fill(""));
  const [loading, setLoading] = useState<boolean>(false);
  const inputsRef = useRef<HTMLInputElement[]>([]);

  const handleChange = (value: string, index: number) => {
    const newOtp = [...otp];
    newOtp[index] = value.replace(/[^0-9]/g, "");
    setOtp(newOtp);

    if (value && index < otp.length - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pasteData = e.clipboardData.getData("Text").trim();
    if (/^\d+$/.test(pasteData)) {
      const digits = pasteData.split("").slice(0, otp.length);
      const newOtp = [...otp];
      digits.forEach((d, i) => {
        newOtp[i] = d;
        if (inputsRef.current[i]) inputsRef.current[i].value = d;
      });
      setOtp(newOtp);
      inputsRef.current[digits.length - 1]?.focus();
      e.preventDefault();
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    const otpCode = otp.join("");
    const payload = { sessionId, otp: otpCode };

    try {
      const res = await fetch(
        "https://core.pluritongues.com/auth/login/verify-2fa",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      const data = await res.json();

      if (data.status && data.result.accessToken) {
        showToast("Account verified successfully!", true);
        await localStorage.setItem("accessToken", data.result.accessToken);

        // Removed redundant storage of email
        await localStorage.removeItem("user_email");
        await localStorage.removeItem("loginSessionId");
        navigate("/dashboard");
      } else {
        showToast(data.message || "Verification failed", false);
      }
    } catch (err) {
      showToast("Something went wrong, please try again.", false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100">
      <div className="bg-white shadow-lg rounded-lg p-8 w-full max-w-md">
        <div className="flex flex-col items-center mb-6">
          <img
            src={logo}
            alt="Pluritongue"
            className="mb-3"
            style={{ width: "150px", marginBottom: "10px", marginTop: "5px" }}
          />
          <h2 className="text-2xl font-bold text-gray-800">
            Lams Account Verification
          </h2>
          <p className="text-gray-600 mt-2 text-center">
            We have sent a code to your email{" "}
            <span className="font-bold">{useEmail}</span>
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="flex justify-center space-x-2">
            {otp.map((digit, i) => (
              <input
                key={i}
                ref={(el) => (inputsRef.current[i] = el!)}
                type="text"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(e.target.value, i)}
                onKeyDown={(e) => handleKeyDown(e, i)}
                onPaste={handlePaste}
                className="w-12 h-12 text-center border border-gray-300 rounded-md text-lg font-semibold focus:outline-none focus:ring-2 focus:ring-brand"
              />
            ))}
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full mt-4 py-2 px-4 bg-brand text-white font-semibold rounded-md shadow hover:bg-brand transition"
          >
            {loading ? "Verifying Account..." : "Verify Account"}
          </button>

          <div className="text-center text-sm text-gray-600">
            <p>Didn't receive code?</p>
            <a href="/login" className="text-brand hover:underline">
              Resend
            </a>
          </div>
        </form>
      </div>
    </div>
  );
}
